import Breadcrumbs from '@/components/base/Breadcrumbs';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import TipForm from '@/components/feature/TipForm';
import { principles, stats, testimonials } from '@/mocks/about';
import { SITE_META, useSeo } from '@/hooks/useSeo';

const SITE_URL = 'https://hnbhcpa.cn';

export default function AboutPage() {
  useSeo({
    title: '关于我们_爆料专区导航站的定位与内容原则',
    description:
      '了解爆料专区导航站的定位、整理理念与内容原则。我们标注信息来源与时间，帮助用户更快找到爆料专区首页入口、网址网页与热门讨论区。',
    keywords: '爆料专区关于我们,爆料专区导航,爆料专区定位,爆料专区内容原则,爆料专区联系方式',
    path: '/about',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于爆料专区导航',
        url: `${SITE_URL}/about`,
        inLanguage: 'zh-CN',
        description: '爆料专区导航站的品牌介绍、定位、内容原则与联系方式。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '爆料专区导航',
        url: `${SITE_URL}/`,
        email: SITE_META.email,
        description: '信息整理与访问导航站点，帮助用户快速找到爆料专区入口与讨论区。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_URL}/about` },
        ],
      },
    ],
  });

  return (
    <main className="pt-16 md:pt-20">
      <section className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          <h1 className="font-heading mt-5 text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            关于爆料专区导航
          </h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
            我们是一个专注于信息整理与访问导航的站点。网络上的爆料往往分散在多个讨论区，找起来费时又费力。爆料专区导航把首页入口、网址网页与热门讨论区归拢到一处，让每一次访问都更直接。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Positioning" title="我们的定位" />
            <div className="mt-6 space-y-4 text-sm md:text-base leading-loose text-foreground-700">
              <p>
                爆料专区导航不做内容生产，只做内容整理。我们每天浏览各个讨论区，把值得关注的线索、正在发酵的话题与常用的访问入口筛选出来，按主题归类，并标注整理时间。
              </p>
              <p>
                之所以坚持标注信息来源，是因为我们相信判断权应该留给读者。一条爆料是否可信、是否值得跟进，最终的判断标准应该来自公开、可核验的信息，而不是某一个人的结论。
              </p>
              <p>
                我们也希望这个站点能降低新用户的上手门槛。无论你是想快速了解今天大家在讨论什么，还是想长期跟进某个方向，都能在这里找到一条清晰的路径。
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((item) => (
                <div key={item.id} className="bg-background-100 border border-background-200 rounded-lg p-6">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-600">{item.value}</p>
                  <p className="mt-2 text-sm text-foreground-600">{item.label}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-secondary-950 rounded-lg p-6 text-secondary-100">
              <h2 className="font-heading text-base font-semibold text-background-50">联系方式</h2>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-mail-line mt-0.5 text-accent-400" />
                  <span className="break-all">{SITE_META.email}</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-time-line mt-0.5 text-accent-400" />
                  <span>内容每日维护 · 更新汇总每周推送</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-100 border-y border-background-200">
        <SectionHeading
          eyebrow="Principles"
          title="内容原则"
          desc="四条我们每天都会对照执行的准则，它们决定了什么内容会被收录，以及以什么样的方式呈现。"
          align="center"
        />
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {principles.map((item, index) => (
            <Reveal key={item.id} delay={index * 50}>
              <article className="h-full bg-background-50 border border-background-200 rounded-lg p-6">
                <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center text-xl">
                  <i className={item.icon} />
                </span>
                <h3 className="font-heading mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <SectionHeading eyebrow="Voices" title="用户怎么说" align="center" />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((voice) => (
            <figure key={voice.id} className="bg-background-50 border border-background-200 rounded-lg p-6 flex flex-col">
              <i className="ri-double-quotes-l text-3xl text-primary-300" />
              <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700 flex-1">
                {voice.content}
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-background-200">
                <span className="block text-sm font-semibold text-foreground-950">{voice.name}</span>
                <span className="mt-0.5 block text-xs text-foreground-500">{voice.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-accent-50 border-t border-accent-200">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Submit a Tip"
              title="投稿你的线索"
              desc="如果你手上有值得关注的线索，欢迎通过下面的表单提交。请尽量写清时间、地点与信息来源，编辑会优先处理信息完整的投稿。"
            />
            <ul className="mt-6 space-y-3 text-sm text-foreground-700">
              <li className="flex items-start gap-2">
                <i className="ri-check-line mt-0.5 text-secondary-600" />
                请勿提交他人隐私信息或未经核实的指控。
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line mt-0.5 text-secondary-600" />
                信息越完整，越容易被编辑采纳与整理。
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line mt-0.5 text-secondary-600" />
                邮箱仅用于编辑回访，不会对外公开。
              </li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
              <TipForm />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}